/**
 * Voice AI — Calls Page (Orchestrator)
 * Owns all state, loads data via callsService, passes props to children.
 * Children dispatch events up; this component handles them.
 */

import { LitElement, html, css } from 'lit';
import { designSystem } from '../../styles/design-system.js';
import { callsStyles } from '../../styles/calls-styles.js';
import { callsService } from '../../services/calls-service.js';
import { AbortableLoader } from '../../services/abortable-loader.js';

import './calls-stats-bar.js';
import './calls-filter-bar.js';
import './calls-table.js';

export class CallsPage extends LitElement {
  static properties = {
    _calls:               { state: true },
    _stats:               { state: true },
    _totalCalls:          { state: true },
    _currentPage:         { state: true },
    _pageSize:            { state: true },
    _searchQuery:         { state: true },
    _activeStatusFilters: { state: true },
    _activeDateRange:     { state: true },
    _activeDirection:     { state: true },
    _sortBy:              { state: true },
    _sortDirection:       { state: true },
    _loading:             { state: true },
    _error:               { state: true },
  };

  static styles = [
    designSystem,
    callsStyles,
    css`
      :host {
        display: block;
        font-family: var(--font-body);
        color: var(--color-text-primary);
      }

      .page-layout {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
        animation: fadeIn var(--duration-slow) var(--ease-out) both;
      }
    `
  ];

  constructor() {
    super();
    this._calls = [];
    this._stats = { total: 0, completedPct: 0, avgDuration: 0, totalMinutes: 0 };
    this._totalCalls = 0;
    this._currentPage = 1;
    this._pageSize = 20;
    this._searchQuery = '';
    this._activeStatusFilters = [];
    this._activeDateRange = 'all';
    this._activeDirection = 'all';
    this._sortBy = 'dateTime';
    this._sortDirection = 'desc';
    this._loading = true;
    this._error = '';
    this._loader = new AbortableLoader();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadCalls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._loader.abort();
  }

  // ── Data Loading ──

  async _loadCalls() {
    this._loading = true;
    this._error = '';

    const result = await this._loader.run(signal =>
      callsService.loadCalls({
        page: this._currentPage,
        limit: this._pageSize,
        search: this._searchQuery,
        status: this._activeStatusFilters.join(','),
        direction: this._activeDirection === 'all' ? '' : this._activeDirection,
        dateRange: this._activeDateRange,
        sort: this._sortBy,
        sortDir: this._sortDirection,
      }, { signal })
    );

    if (result.aborted) return;

    if (result.error) {
      if (result.error.message !== 'Session expired') {
        this._error = 'Failed to load call history';
        console.error('[CallsPage]', result.error);
      }
    } else if (!result.data.success) {
      this._error = result.data.error || 'Failed to load calls';
    } else {
      this._calls = result.data.calls || [];
      this._totalCalls = result.data.pagination?.total || 0;
      this._stats = result.data.stats || { total: 0, completedPct: 0, avgDuration: 0, totalMinutes: 0 };
    }

    this._loading = false;
  }

  // ── Event Handlers (from children) ──

  _onSearch(e) {
    this._searchQuery = e.detail.query;
    this._currentPage = 1;
    this._loadCalls();
  }

  _onFilterStatus(e) {
    this._activeStatusFilters = e.detail.statuses;
    this._currentPage = 1;
    this._loadCalls();
  }

  _onFilterDate(e) {
    this._activeDateRange = e.detail.range;
    this._currentPage = 1;
    this._loadCalls();
  }

  _onFilterDirection(e) {
    this._activeDirection = e.detail.direction;
    this._currentPage = 1;
    this._loadCalls();
  }

  _onSort(e) {
    const column = e.detail.column;
    if (this._sortBy === column) {
      this._sortDirection = this._sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortBy = column;
      this._sortDirection = 'desc';
    }
    this._currentPage = 1;
    this._loadCalls();
  }

  _onPageChange(e) {
    this._currentPage = e.detail.page;
    this._loadCalls();
  }

  // ── Render ──

  render() {
    return html`
      <div class="page-layout">
        ${this._error ? html`
          <div class="error-banner">
            <i class="fa-sharp fa-solid fa-circle-exclamation"></i>
            <span>${this._error}</span>
            <button class="retry-btn" @click="${() => this._loadCalls()}">Retry</button>
          </div>
        ` : ''}

        <calls-filter-bar
          .searchQuery="${this._searchQuery}"
          .activeStatusFilters="${this._activeStatusFilters}"
          .activeDateRange="${this._activeDateRange}"
          .activeDirection="${this._activeDirection}"
          @search="${this._onSearch}"
          @filter-status="${this._onFilterStatus}"
          @filter-date="${this._onFilterDate}"
          @filter-direction="${this._onFilterDirection}"
        ></calls-filter-bar>

        <calls-stats-bar
          .stats="${this._stats}"
          .loading="${this._loading}"
        ></calls-stats-bar>

        <calls-table
          .calls="${this._calls}"
          .loading="${this._loading}"
          .sortBy="${this._sortBy}"
          .sortDirection="${this._sortDirection}"
          .currentPage="${this._currentPage}"
          .totalCalls="${this._totalCalls}"
          .pageSize="${this._pageSize}"
          @sort="${this._onSort}"
          @page-change="${this._onPageChange}"
        ></calls-table>
      </div>
    `;
  }
}

customElements.define('calls-page', CallsPage);
